import { useEffect, useMemo, useCallback, useRef } from 'react';
import UIEmbeddedComponent from 'ui-addon-iframeable/embed/UIEmbeddedComponent';
import { MSG_TYPE_READY } from 'ui-addon-iframeable/messaging/IFrameControlMessage';
import { EMBEDDED_IN_APP_NAME_DATA_ATTRIBUTE, MARKETING_STUDIO_APP, MESSAGE_TYPES } from './constants';
import { getIsInIframe, getIsTestingEmbeddedVersionOutsideIframe } from './utils';
import { EmbeddedContext } from './EmbeddedEditorContext';
import { navigateToAnchor } from './AnchorNavigation';
import devLogger from 'react-utils/devLogger';
import { jsx as _jsx } from "react/jsx-runtime";
const getTestEmbeddedContext = () => {
  if (!getIsTestingEmbeddedVersionOutsideIframe()) {
    return;
  }
  console.log('Providing a test embedded context');
  return {
    app: {
      name: MARKETING_STUDIO_APP,
      info: {},
      url: ''
    },
    sendMessage: (...args) => console.log('sendMessage to parent frame', args),
    sendBroadcastMessage: (...args) => console.log('sendBroadcastMessage to parent frame', args),
    sendDirectedMessage: (...args) => console.log('sendDirectedMessage to parent frame', args),
    sendBroadcastReplyMessage: (...args) => console.log('sendBroadcastReplyMessage to parent frame', args),
    sendReplyMessage: (...args) => console.log('sendReplyMessage to parent frame', args),
    id: 'test-id',
    group: 'test-group',
    name: 'test-name'
  };
};
const EmbeddedWrapper = ({
  children,
  embeddedContext,
  // Provide custom navigation handlers for anchor navigation
  anchorNavigation,
  appName
}) => {
  var _embeddedContext, _embeddedContext2;
  if (getIsTestingEmbeddedVersionOutsideIframe()) {
    // eslint-disable-next-line react-hooks/immutability
    embeddedContext = getTestEmbeddedContext();
  }
  const isInsideMarketingStudio = ((_embeddedContext = embeddedContext) === null || _embeddedContext === void 0 ? void 0 : _embeddedContext.app.name) === MARKETING_STUDIO_APP;
  const shouldRunLocalEditor = (_embeddedContext2 = embeddedContext) === null || _embeddedContext2 === void 0 || (_embeddedContext2 = _embeddedContext2.app.info) === null || _embeddedContext2 === void 0 ? void 0 : _embeddedContext2.useLocalEditor;
  const isLocalEditor = shouldRunLocalEditor && window.location.pathname.includes('local.hubspot');
  const requiresReload = !isLocalEditor && shouldRunLocalEditor;
  const handleAppStarting = useCallback(() => {
    var _embeddedContext3;
    if (!isInsideMarketingStudio) return;
    let appNameForMessage = appName;
    if (!appName) {
      const elementWithAppName = document.querySelector('[data-application-name]');
      if (elementWithAppName) {
        appNameForMessage = elementWithAppName.dataset.applicationName;
        elementWithAppName.dataset[EMBEDDED_IN_APP_NAME_DATA_ATTRIBUTE] = MARKETING_STUDIO_APP;
      }
    }
    devLogger.warn({
      message: `Sending app starting message: ${appNameForMessage}`,
      key: 'embedded-app-starting'
    });
    (_embeddedContext3 = embeddedContext) === null || _embeddedContext3 === void 0 || _embeddedContext3.sendMessage(MESSAGE_TYPES.APP_STARTING, {
      embeddedAppName: appNameForMessage,
      embeddedAppUrl: window.location.href
    }, () => {});
  }, [isInsideMarketingStudio, embeddedContext, appName]);
  const hasSentAppStarting = useRef(false);

  // Let the parent know the child wrapper has mounted
  useEffect(() => {
    if (isInsideMarketingStudio && embeddedContext && !hasSentAppStarting.current) {
      hasSentAppStarting.current = true;
      handleAppStarting();
    }
  }, [isInsideMarketingStudio, embeddedContext, handleAppStarting]);
  const handleAppIsReady = useCallback(() => {
    var _embeddedContext4;
    // Navigates to anchors provided by the parent component
    navigateToAnchor({
      anchorNavigation
    });
    if (!isInsideMarketingStudio) return;
    const navBar = document.getElementById('hs-global-toolbar-root');
    if (navBar) {
      navBar.style.display = 'none';
    }
    const pageElement = document.querySelector('.page.quartz-grid-layout');
    if (pageElement) {
      const pageStyles = getComputedStyle(pageElement);
      if (pageStyles.width.includes('calc')) {
        pageElement.style.width = 'auto';
      }
    }
    (_embeddedContext4 = embeddedContext) === null || _embeddedContext4 === void 0 || _embeddedContext4.sendMessage(MSG_TYPE_READY, {}, () => {});
  }, [anchorNavigation, embeddedContext, isInsideMarketingStudio]);
  const handleAppOnPublish = useCallback(publishData => {
    var _embeddedContext5;
    (_embeddedContext5 = embeddedContext) === null || _embeddedContext5 === void 0 || _embeddedContext5.sendMessage(MESSAGE_TYPES.ON_PUBLISH, {
      publishData
    }, () => {});
  }, [embeddedContext]);
  const handleAppNavigate = useCallback(navigateData => {
    var _embeddedContext6;
    (_embeddedContext6 = embeddedContext) === null || _embeddedContext6 === void 0 || _embeddedContext6.sendMessage(MESSAGE_TYPES.NAVIGATE, {
      navigateData
    }, () => {});
  }, [embeddedContext]);
  const handleAppError = useCallback((error, message) => {
    var _embeddedContext7;
    (_embeddedContext7 = embeddedContext) === null || _embeddedContext7 === void 0 || _embeddedContext7.sendMessage(MESSAGE_TYPES.ERROR, {
      error,
      message
    }, () => {});
  }, [embeddedContext]);
  const handleCloseEditor = useCallback(() => {
    var _embeddedContext8;
    (_embeddedContext8 = embeddedContext) === null || _embeddedContext8 === void 0 || _embeddedContext8.sendMessage(MESSAGE_TYPES.CLOSE_EDITOR, {}, () => {});
  }, [embeddedContext]);
  const finalEmbeddedContext = useMemo(() => {
    if (!isInsideMarketingStudio) {
      return {
        isInsideMarketingStudio: false
      };
    }
    return {
      embeddedContext,
      handleAppIsReady,
      handleAppNavigate,
      handleAppOnPublish,
      handleCloseEditor,
      handleAppError,
      anchorNavigation,
      isInsideMarketingStudio
    };
  }, [embeddedContext, handleAppIsReady, handleAppNavigate, handleAppOnPublish, handleAppError, handleCloseEditor, anchorNavigation, isInsideMarketingStudio]);
  useEffect(() => {
    if (requiresReload) {
      const newUrl = window.location.href.replace('app.hubspot', 'local.hubspot');
      window.location.href = newUrl;
      return;
    }
    const handleUnload = e => {
      var _embeddedContext9;
      e.preventDefault();
      hasSentAppStarting.current = false;
      (_embeddedContext9 = embeddedContext) === null || _embeddedContext9 === void 0 || _embeddedContext9.sendMessage(MESSAGE_TYPES.UNLOAD, {}, () => {});
    };
    if (embeddedContext) {
      window.addEventListener('unload', handleUnload);
    }
    return () => {
      if (embeddedContext) {
        window.removeEventListener('unload', handleUnload);
      }
    };
  }, [embeddedContext, requiresReload]);
  return /*#__PURE__*/_jsx(EmbeddedContext.Provider, {
    value: finalEmbeddedContext,
    children: children
  });
};
export const TestableComponent = ({
  children
}) => {
  const Wrapper = getIsInIframe() ?
  // @ts-ignore FIXME 2026-05-11 Issue found due to ui-addon-iframeable TS migration (see #311). See `#typesafe-ui-addon-iframeable` Slack channel for more details.
  UIEmbeddedComponent(EmbeddedWrapper) : EmbeddedWrapper;
  // eslint-disable-next-line react-hooks/static-components
  return /*#__PURE__*/_jsx(Wrapper, {
    children: children
  });
};
export default getIsInIframe() ?
// @ts-ignore FIXME 2026-05-11 Issue found due to ui-addon-iframeable TS migration (see #311). See `#typesafe-ui-addon-iframeable` Slack channel for more details.
UIEmbeddedComponent(EmbeddedWrapper, {
  enableTopLevelEmbedForTestOnly: true
}) : EmbeddedWrapper;